import DoneIcon from "@mui/icons-material/DoneTwoTone";
import { Autocomplete, css, TextField } from "@mui/material";
import { useState } from "react";
import { theme } from "../../design/theme";
import { FlexRowContainer } from "./layout";
import { ALL_TAGS_OPTION, NO_TAGS_OPTION } from "./tags/constants";
import { TagManagementModal } from "./tags/TagManagementModal";

type TagOption = { id: number | string; name: string };

interface TagsFilterMenuProps {
  tags?: TagOption[];
  selectedTags?: TagOption[];
  setSelectedTags?: (tags: TagOption[]) => void;
  hideTagManagement?: boolean;
}

export const TagsFilterMenu = ({
  tags = [],
  selectedTags = [],
  setSelectedTags,
  hideTagManagement = false,
}: TagsFilterMenuProps) => {
  const [internalSelectedTags, setInternalSelectedTags] = useState<TagOption[]>(
    [],
  );

  const activeSelectedTags = selectedTags.length
    ? selectedTags
    : internalSelectedTags;

  const sortedTags = [...tags].sort((a, b) => a.name.localeCompare(b.name));

  const options = [
    ALL_TAGS_OPTION,
    NO_TAGS_OPTION,
    ...sortedTags.map((tag) => ({ id: tag.id, name: tag.name })),
  ];

  const setTags = (nextTags: TagOption[]) => {
    if (setSelectedTags) {
      setSelectedTags(nextTags);
      return;
    }
    setInternalSelectedTags(nextTags);
  };

  const handleChange = (
    _: unknown,
    value: { id: number | string; name: string }[],
  ) => {
    if (value.find((option) => option.id === ALL_TAGS_OPTION.id)) {
      setTags([]);
      return;
    }
    setTags(value as TagOption[]);
  };

  return (
    <FlexRowContainer>
      <div
        css={({ customTypography }) => css`
          ${customTypography.paragraph._3}
        `}
      >
        Tags:
      </div>
      <Autocomplete
        multiple
        options={options}
        getOptionLabel={(option) => option.name}
        value={activeSelectedTags}
        onChange={handleChange}
        isOptionEqualToValue={(opt, val) => opt.id === val.id}
        disableCloseOnSelect
        size="small"
        renderOption={(props, option, { selected }) => {
          return (
            <li {...props} key={option.id}>
              <FlexRowContainer
                style={{ justifyContent: "space-between", width: "100%" }}
              >
                <span
                  style={{
                    ...theme.customTypography.paragraph._3,
                  }}
                >
                  {option.name}
                </span>
                {(selected ||
                  (activeSelectedTags.length === 0 &&
                    option.id === ALL_TAGS_OPTION.id)) && (
                  <DoneIcon fontSize="small" />
                )}
              </FlexRowContainer>
            </li>
          );
        }}
        renderInput={(params) => {
          const hasSelected = activeSelectedTags.length > 0;
          const modifiedParams = {
            ...params,
            InputProps: {
              ...params.InputProps,
              startAdornment: hasSelected ? (
                params.InputProps.startAdornment
              ) : (
                <span
                  style={{
                    marginLeft: 8,
                    ...theme.customTypography.paragraph._3,
                  }}
                >
                  All
                </span>
              ),
            },
          };

          return <TextField {...modifiedParams} size="small" />;
        }}
        renderTags={() => (
          <span style={theme.customTypography.paragraph._3}>
            {`${activeSelectedTags.length} selected`}
          </span>
        )}
        css={({ breakpoints }) => css`
          width: 250px;
          @media (max-width: ${breakpoints.values.xl}px) {
            width: 150px;
          }
        `}
      />
      {!hideTagManagement && (
        <TagManagementModal
          tags={sortedTags as { id: number; name: string }[]}
        />
      )}
    </FlexRowContainer>
  );
};
